import { css, html, LitElement, nothing } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { Dialog } from "../dialog";

import { Checkbox, CheckboxButton } from "./../checkbox";
import { shared } from "../../styles";
import { formatDate } from "@pentacode/core";
import { DateString } from "@pentacode/openapi";

type Props = {
    termsAccepted: boolean;
    processingAgreementAccepted: boolean;
    deadline: DateString;
};

@customElement("ptc-accept-terms-form")
export class AcceptTermsForm extends LitElement {
    @property({ type: Boolean })
    termsWereAccepted = false;

    @property({ type: Boolean })
    processingAgreementWasAccepted = false;

    @property({ attribute: false })
    deadline: DateString;

    @query("ptc-checkbox-button[name='terms']")
    private _termsCheckbox: CheckboxButton;

    @query("ptc-checkbox-button[name='processingAgreement']")
    private _processingAgreementCheckbox: CheckboxButton;

    static styles = [
        shared,
        Checkbox.styles,
        css`
            .inner {
                padding: 1em;
            }

            .left-margin {
                margin-left: 0.7em;
            }

            .warning {
                color: var(--color-negative);
            }
        `,
    ];

    render() {
        const acceptanceOverdue = new Date() >= new Date(this.deadline);
        const termsLabel = this.termsWereAccepted ? `AGB bereits akzeptiert` : "AGB akzeptieren";
        const processingAgreementLabel = this.processingAgreementWasAccepted
            ? `Bearbeitungsvertrag bereits akzeptiert`
            : "Bearbeitungsvertrag akzeptieren";
        const cancelLabel = acceptanceOverdue ? "Abmelden" : "Später Entscheiden";

        return html`<h3>${this.termsWereAccepted ? nothing : "AGB und "} Bearbeitungsvertrag akzeptieren</h3>

            <p class="text left-margined">
                Der Gesetzgeber verpflichtet, die Kenntnisnahme
                ${this.termsWereAccepted
                    ? nothing
                    : html`unserer
                          <a href="https://pentacode.app/dokumente/Pentacode_AGB.pdf" target="_blank"
                              >Allgemeinen Geschäftsbedingungen (AGB)</a
                          >
                          und `}
                unseres
                <a href="https://pentacode.app/dokumente/Pentacode_Bearbeitungsvertrag.pdf" target="_blank"
                    >Bearbeitungsvertrages</a
                >
                zu bestätigen.
            </p>
            ${acceptanceOverdue
                ? html`<p class="small subtle left-margined top-margined red colored-text">
                      Da Pentacode personenbezogene Daten verarbeitet, ist die Nutzung von Pentacode ohne Bestätigung
                      leider nicht möglich.
                  </p>`
                : html`<p class="small subtle left-margined top-margined red colored-text">
                      Für eine unterbrechungsfreie Nutzung von Pentacode bitten wir um Bestätigung bis spätestens
                      <span class="bold">${formatDate(this.deadline)}</span>.
                  </p>`}

            <form class="vertical layout" @submit=${this._onSubmit}>
                <ptc-checkbox-button
                    name="terms"
                    buttonClass="transparent"
                    ?readonly=${this.termsWereAccepted}
                    ?disabled=${this.termsWereAccepted}
                    ?checked=${this.termsWereAccepted}
                    @change=${() => this.requestUpdate()}
                    label=${termsLabel}
                    style=${this.termsWereAccepted ? "display: none;" : ""}
                ></ptc-checkbox-button>

                <ptc-checkbox-button
                    name="processingAgreement"
                    buttonClass="transparent"
                    ?readonly=${this.processingAgreementWasAccepted}
                    ?disabled=${this.processingAgreementWasAccepted}
                    ?checked=${this.processingAgreementWasAccepted}
                    @change=${() => this.requestUpdate()}
                    label=${processingAgreementLabel}
                ></ptc-checkbox-button>

                <div class="horizontal layout actions">
                    <button
                        class="primary stretch"
                        ?disabled=${!this._termsCheckbox?.checked || !this._processingAgreementCheckbox?.checked}
                        @click=${this._onConfirmed}
                    >
                        Bestätigen
                    </button>
                    <button class="subtle stretch red left-margin" @click=${this._onDismissed}>
                        <i class="times"></i> ${cancelLabel}
                    </button>
                </div>
            </form>`;
    }

    private _onSubmit(event: Event) {
        event.preventDefault();
        this._onConfirmed();
    }

    private _onDismissed() {
        this.dispatchEvent(new CustomEvent("dismissed"));
    }

    private _onConfirmed() {
        if (!this._termsCheckbox.checked) {
            this._termsCheckbox.setCustomValidity("Bitte akzeptieren Sie die AGB.");
            this._termsCheckbox.reportValidity();
            return;
        }

        if (!this._processingAgreementCheckbox.checked) {
            this._processingAgreementCheckbox.setCustomValidity("Bitte akzeptieren Sie den Bearbeitungsvertrag.");
            this._processingAgreementCheckbox.reportValidity();
            return;
        }

        this.dispatchEvent(new CustomEvent("accepted"));
    }
}

@customElement("ptc-accept-terms-dialog")
export class AcceptTermsDialog extends Dialog<Props, boolean> {
    readonly preventDismiss = true;

    static styles = [shared, ...Dialog.styles];

    renderContent() {
        return html`
            <div class="vertical padded layout">
                <ptc-accept-terms-form
                    .termsWereAccepted=${this.input.termsAccepted}
                    .processingAgreementWasAccepted=${this.input.processingAgreementAccepted}
                    .deadline=${this.input.deadline}
                    @accepted=${() => this.done(true)}
                    @dismissed=${() => this.done(false)}
                >
                </ptc-accept-terms-form>
            </div>
        `;
    }
}
